/**
 * "Wrong customer? Move this job" (Issue #1897).
 *
 * Job #102678651 was FrontStreet's work order built under Commercial Fire. The
 * WO filled on Commercial Fire's paper, the report named Commercial Fire, and
 * the packet was addressed to Commercial Fire — and nothing in the app could
 * fix it. This file is the office's way out, on the job page and in Closeout:
 *
 *   MoveJobLink        the small "Wrong customer?" button by the customer name
 *   JobCustomerCheck   the warning when the paper names someone else, and the
 *                      "Moved today — Undo" strip after a move
 *   MoveJobDialog      pick the customer → pick the place → see the plan → one button
 *
 * Everything the office reads is the server's plan in plain words; nothing is
 * changed until the one button. Visual first: what moves is green, what stays
 * is gray, what gets redone is blue, what stops it is red.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ArrowRight,
    Ban,
    Building2,
    CheckCircle2,
    FileText,
    Home,
    Loader2,
    Mail,
    MapPin,
    Package,
    RefreshCw,
    Receipt,
    TriangleAlert,
    Undo2,
    Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AsyncPicker } from "@/components/async-picker";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/** Auth when there is a provider, else null — these widgets also render in
 *  provider-less places (page tests, previews) and simply hide there.
 *  useAuth always calls useContext first, so hook order never changes. */
function useOptionalAuth(): ReturnType<typeof useAuth> | null {
    try {
        return useAuth();
    } catch {
        return null;
    }
}
import { PERMISSIONS } from "@/lib/permissions";
import { cn } from "@/lib/utils";

// ─── Shapes (mirror services/jobCustomerMove.ts) ──────────────────────────

export interface MoveNotice {
    code: string;
    message: string;
}
export interface PlanDevice {
    id: string;
    name: string;
    type: string | null;
    serial: string | null;
    location: string | null;
    action: "move" | "merge" | "stays_with_site";
    mergeInto: { id: string; name: string; serial: string | null } | null;
}
export interface SiteChoice {
    id: string;
    name: string;
    address: string;
    city: string | null;
    sameAddress: boolean;
    deviceCount: number;
}
export interface MovePlan {
    job: { id: string; displayNumber: string | null; title: string | null; status: string; kind: string | null };
    from: {
        customerId: string | null;
        customerName: string | null;
        siteId: string | null;
        siteName: string | null;
        address: string;
    };
    to: {
        customerId: string;
        customerName: string;
        siteId: string | null;
        siteName: string | null;
        address: string;
        siteMode: "whole_site" | "into_site" | "new_site";
    };
    siteChoices: SiteChoice[];
    oldSiteOnlyThisJob: boolean;
    devices: PlanDevice[];
    testCount: number;
    invoices: Array<{ id: string; number: string | null; total: number; status: string }>;
    estimates: Array<{ id: string; number: string | null; total: number; status: string }>;
    attachmentCount: number;
    heldPacket: { recipients: string[] } | null;
    stays: string[];
    rebuild: string[];
    packetTo: string[];
    packetFrom: string[];
    blockers: MoveNotice[];
    warnings: MoveNotice[];
    canMove: boolean;
}
export interface RebuildOutcome {
    workOrder: { step: string; message: string };
    reports: { step: string; message: string };
}
export interface MoveRecord {
    id: string;
    fromCustomerName: string | null;
    toCustomerName: string | null;
    movedAt: string;
    movedByName: string | null;
    undoneAt: string | null;
    canUndo: boolean;
    undoBlockedReason: string | null;
    rebuild: RebuildOutcome | null;
}
export interface IssuerCheck {
    printedName: string | null;
    paperCustomerId: string | null;
    paperCustomerName: string | null;
    customerId: string | null;
    customerName: string | null;
    customerIsVendor: boolean;
    mismatch: boolean;
}
export interface MoveStatus {
    lastMove: MoveRecord | null;
    issuer: IssuerCheck;
}

export function useJobMoveStatus(jobId: string | null | undefined) {
    return useQuery({
        queryKey: ["job-move-customer", jobId],
        queryFn: () => api.get<MoveStatus>(`/api/jobs/${jobId}/move-customer`),
        enabled: !!jobId,
        staleTime: 30_000,
    });
}

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function sameDay(iso: string): boolean {
    const d = new Date(iso);
    const now = new Date();
    return d.toDateString() === now.toDateString();
}

// ─── The dialog ───────────────────────────────────────────────────────────

export function MoveJobDialog({
    jobId,
    open,
    onClose,
    suggestedCustomer,
}: {
    jobId: string;
    open: boolean;
    onClose: () => void;
    /** The customer the WO paper names, when it resolves — offered first. */
    suggestedCustomer?: { id: string; name: string } | null;
}) {
    const qc = useQueryClient();
    const statusQ = useJobMoveStatus(open && suggestedCustomer === undefined ? jobId : null);
    const paper = statusQ.data?.issuer ?? null;
    const suggestion =
        suggestedCustomer !== undefined
            ? suggestedCustomer
            : paper?.paperCustomerId && paper.paperCustomerId !== paper.customerId
              ? { id: paper.paperCustomerId, name: paper.paperCustomerName ?? paper.printedName ?? "Customer" }
              : null;
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [siteId, setSiteId] = useState<string | null>(null);
    const [done, setDone] = useState<{ rebuild: RebuildOutcome; lastMove: MoveRecord | null; to: string } | null>(null);

    useEffect(() => {
        if (open) {
            setCustomerId(null);
            setSiteId(null);
            setDone(null);
        }
    }, [open]);

    const previewQ = useQuery({
        queryKey: ["job-move-preview", jobId, customerId, siteId],
        queryFn: () =>
            api.post<{ plan: MovePlan }>(`/api/jobs/${jobId}/move-customer/preview`, {
                toCustomerId: customerId,
                toSiteId: siteId,
            }),
        enabled: open && !!customerId && !done,
        staleTime: 0,
    });
    const plan = previewQ.data?.plan ?? null;

    // First look at a customer: if they already have a place at this address,
    // pick it for the office — that is almost always the right answer.
    useEffect(() => {
        if (!plan || siteId !== null) return;
        const same = plan.siteChoices.filter((s) => s.sameAddress);
        if (same.length === 1 && plan.to.siteMode !== "into_site") setSiteId(same[0]!.id);
    }, [plan, siteId]);

    const move = useMutation({
        mutationFn: () =>
            api.post<{ ok: true; rebuild: RebuildOutcome; lastMove: MoveRecord | null; plan: MovePlan }>(
                `/api/jobs/${jobId}/move-customer`,
                { toCustomerId: customerId, toSiteId: siteId },
                { timeoutMs: 180_000 },
            ),
        onSuccess: (r) => {
            setDone({ rebuild: r.rebuild, lastMove: r.lastMove, to: r.plan.to.customerName });
            void qc.invalidateQueries();
            toast.success(`Job moved to ${r.plan.to.customerName}.`);
        },
        onError: (err) => {
            const msg = err instanceof ApiError ? ((err.body as { message?: string } | null)?.message ?? null) : null;
            toast.error(msg ?? "The job didn't move. Nothing was changed.");
            void previewQ.refetch();
        },
    });

    const busy = move.isPending;

    return (
        <Dialog
            open={open}
            onClose={busy ? () => undefined : onClose}
            title="Move this job to the right customer"
            className="max-w-2xl"
        >
            {done ? (
                <MoveDone done={done} jobId={jobId} onClose={onClose} />
            ) : (
                <div className="space-y-5" data-testid="move-job-dialog">
                    {/* Step 1 — who */}
                    <section className="space-y-2">
                        <StepTitle n={1} text="Who is this job really for?" />
                        {suggestion && customerId !== suggestion.id && (
                            <button
                                type="button"
                                onClick={() => {
                                    setCustomerId(suggestion.id);
                                    setSiteId(null);
                                }}
                                className="flex w-full items-center gap-3 rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 text-left hover:bg-emerald-100"
                                data-testid="move-job-suggested"
                            >
                                <FileText className="h-6 w-6 shrink-0 text-emerald-700" />
                                <span className="min-w-0">
                                    <span className="block text-sm font-bold text-emerald-900">{suggestion.name}</span>
                                    <span className="block text-xs text-emerald-800">
                                        The work order paper says this company
                                    </span>
                                </span>
                            </button>
                        )}
                        <AsyncPicker
                            value={customerId}
                            onChange={(id) => {
                                setCustomerId(id);
                                setSiteId(null);
                            }}
                            placeholder="Find a customer…"
                            queryKeyBase="move-job-customer"
                            buildSearchPath={(q) => `/api/customers?q=${encodeURIComponent(q)}&limit=20`}
                            mapResults={(data) =>
                                ((data as { customers?: { id: string; company_name: string }[] }).customers ?? []).map(
                                    (c) => ({ id: c.id, label: c.company_name }),
                                )
                            }
                            buildResolvePath={(id) => `/api/customers/${id}`}
                            mapResolvedLabel={(data) =>
                                (data as { customer?: { company_name?: string } }).customer?.company_name ?? "Customer"
                            }
                        />
                    </section>

                    {customerId && previewQ.isLoading && (
                        <p className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" /> Looking at what would move…
                        </p>
                    )}
                    {customerId && previewQ.isError && (
                        <p className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                            {(previewQ.error instanceof ApiError &&
                                (previewQ.error.body as { message?: string } | null)?.message) ||
                                "We couldn't look this up. Try again."}
                        </p>
                    )}

                    {plan && (
                        <>
                            {/* Step 2 — where */}
                            <section className="space-y-2">
                                <StepTitle n={2} text={`Which ${plan.to.customerName} place?`} />
                                <div className="grid gap-2 sm:grid-cols-2">
                                    {plan.siteChoices.slice(0, 8).map((s) => (
                                        <PlaceCard
                                            key={s.id}
                                            selected={siteId === s.id}
                                            onClick={() => setSiteId(s.id)}
                                            icon={<Building2 className="h-5 w-5" />}
                                            title={s.name}
                                            line={s.address || "No address on file"}
                                            chip={s.sameAddress ? "Same address" : null}
                                            foot={
                                                s.deviceCount > 0 ? plural(s.deviceCount, "device") : "No devices yet"
                                            }
                                        />
                                    ))}
                                    <PlaceCard
                                        selected={siteId === null}
                                        onClick={() => setSiteId(null)}
                                        icon={<Home className="h-5 w-5" />}
                                        title="Bring this place over"
                                        line={plan.from.address || plan.from.siteName || "The job's place"}
                                        chip={null}
                                        foot={
                                            plan.oldSiteOnlyThisJob
                                                ? "Only this job used it — it moves as is"
                                                : "Other jobs use it — we copy the address"
                                        }
                                    />
                                </div>
                                {plan.siteChoices.length > 8 && (
                                    <p className="text-xs text-muted-foreground">
                                        Showing the first 8 places. Places at the same address are first.
                                    </p>
                                )}
                            </section>

                            {/* Step 3 — the plan */}
                            <section className="space-y-3">
                                <StepTitle n={3} text="Check what happens" />
                                <FromTo plan={plan} />
                                <PlanLists plan={plan} />
                            </section>

                            <div className="sticky bottom-0 -mx-5 -mb-5 flex flex-wrap items-center justify-end gap-2 border-t bg-card px-5 py-3">
                                <Button variant="ghost" onClick={onClose} disabled={busy}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={() => move.mutate()}
                                    disabled={!plan.canMove || busy || previewQ.isFetching}
                                    data-testid="move-job-confirm"
                                >
                                    {busy ? (
                                        <>
                                            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Moving and redoing the
                                            paperwork…
                                        </>
                                    ) : (
                                        <>Move job to {plan.to.customerName}</>
                                    )}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            )}
        </Dialog>
    );
}

function StepTitle({ n, text }: { n: number; text: string }) {
    return (
        <h3 className="flex items-center gap-2 text-sm font-bold">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                {n}
            </span>
            {text}
        </h3>
    );
}

function PlaceCard(props: {
    selected: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    title: string;
    line: string;
    chip: string | null;
    foot: string;
}) {
    return (
        <button
            type="button"
            onClick={props.onClick}
            aria-pressed={props.selected}
            className={cn(
                "flex min-w-0 items-start gap-2.5 rounded-lg border-2 bg-card p-3 text-left transition-colors",
                props.selected ? "border-primary bg-secondary" : "border-border hover:border-foreground/40",
            )}
        >
            <span className={cn("mt-0.5 shrink-0", props.selected ? "text-primary" : "text-muted-foreground")}>
                {props.icon}
            </span>
            <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-1.5">
                    <span className="truncate text-sm font-bold">{props.title}</span>
                    {props.chip && (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10.5px] font-bold text-emerald-800">
                            {props.chip}
                        </span>
                    )}
                </span>
                <span className="block truncate text-xs text-muted-foreground">{props.line}</span>
                <span className="block text-xs text-muted-foreground">{props.foot}</span>
            </span>
        </button>
    );
}

function FromTo({ plan }: { plan: MovePlan }) {
    return (
        <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2" data-testid="move-job-from-to">
            <div className="min-w-0 rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-[10.5px] font-bold uppercase tracking-wide text-red-800">Now</p>
                <p className="truncate text-sm font-bold text-red-950 line-through decoration-red-400">
                    {plan.from.customerName ?? "No customer"}
                </p>
                <p className="truncate text-xs text-red-900">{plan.from.siteName ?? ""}</p>
            </div>
            <ArrowRight className="h-6 w-6 self-center text-muted-foreground" aria-hidden />
            <div className="min-w-0 rounded-lg border border-emerald-300 bg-emerald-50 p-3">
                <p className="text-[10.5px] font-bold uppercase tracking-wide text-emerald-800">After</p>
                <p className="truncate text-sm font-bold text-emerald-950">{plan.to.customerName}</p>
                <p className="truncate text-xs text-emerald-900">
                    {plan.to.siteMode === "new_site" ? "A copy of this place" : (plan.to.siteName ?? "")}
                </p>
            </div>
        </div>
    );
}

function Row({
    icon,
    children,
    tone,
}: {
    icon: React.ReactNode;
    children: React.ReactNode;
    tone: "go" | "stay" | "redo" | "warn" | "stop";
}) {
    const toneCls = {
        go: "text-emerald-700",
        stay: "text-muted-foreground",
        redo: "text-sky-700",
        warn: "text-amber-700",
        stop: "text-red-700",
    }[tone];
    return (
        <li className="flex items-start gap-2 text-sm">
            <span className={cn("mt-0.5 shrink-0", toneCls)}>{icon}</span>
            <span className="min-w-0">{children}</span>
        </li>
    );
}

function Box({
    title,
    tone,
    children,
}: {
    title: string;
    tone: "go" | "stay" | "redo" | "warn" | "stop";
    children: React.ReactNode;
}) {
    const cls = {
        go: "border-emerald-200",
        stay: "border-border",
        redo: "border-sky-200",
        warn: "border-amber-300 bg-amber-50",
        stop: "border-red-300 bg-red-50",
    }[tone];
    return (
        <div className={cn("rounded-lg border bg-card p-3", cls)}>
            <p className="mb-1.5 text-xs font-extrabold uppercase tracking-wide text-muted-foreground">{title}</p>
            <ul className="space-y-1.5">{children}</ul>
        </div>
    );
}

export function PlanLists({ plan }: { plan: MovePlan }) {
    const merges = plan.devices.filter((d) => d.action === "merge");
    const moving = plan.devices.filter((d) => d.action === "move");
    const withSite = plan.devices.filter((d) => d.action === "stays_with_site");
    return (
        <div className="space-y-2.5" data-testid="move-job-plan">
            {plan.blockers.length > 0 && (
                <Box title="This can't move yet" tone="stop">
                    {plan.blockers.map((b) => (
                        <Row key={b.code + b.message} icon={<Ban className="h-4 w-4" />} tone="stop">
                            {b.message}
                        </Row>
                    ))}
                </Box>
            )}
            <Box title="Moves to the new customer" tone="go">
                <Row icon={<Wrench className="h-4 w-4" />} tone="go">
                    Job #{plan.job.displayNumber ?? "—"}
                    {plan.job.title ? ` — ${plan.job.title}` : ""}
                </Row>
                {plan.to.siteMode === "whole_site" && (
                    <Row icon={<MapPin className="h-4 w-4" />} tone="go">
                        The place itself, with its {plural(withSite.length, "device")}
                    </Row>
                )}
                {moving.length > 0 && (
                    <Row icon={<Package className="h-4 w-4" />} tone="go">
                        {plural(moving.length, "device")} from this job
                    </Row>
                )}
                {merges.map((d) => (
                    <Row key={d.id} icon={<Package className="h-4 w-4" />} tone="go">
                        {d.name}
                        {d.serial ? ` (${d.serial})` : ""} joins the matching {d.mergeInto?.name ?? "device"} already
                        there
                    </Row>
                ))}
                {plan.testCount > 0 && (
                    <Row icon={<CheckCircle2 className="h-4 w-4" />} tone="go">
                        {plural(plan.testCount, "test")}
                    </Row>
                )}
                {plan.invoices.map((i) => (
                    <Row key={i.id} icon={<Receipt className="h-4 w-4" />} tone="go">
                        Draft invoice{i.number ? ` #${i.number}` : ""} — {money(i.total)}
                    </Row>
                ))}
                {plan.estimates.map((e) => (
                    <Row key={e.id} icon={<Receipt className="h-4 w-4" />} tone="go">
                        Estimate{e.number ? ` #${e.number}` : ""} — {money(e.total)}
                    </Row>
                ))}
                {plan.attachmentCount > 0 && (
                    <Row icon={<FileText className="h-4 w-4" />} tone="go">
                        {plural(plan.attachmentCount, "file")} and photos on the job
                    </Row>
                )}
            </Box>
            <Box title="We redo these for you" tone="redo">
                {plan.rebuild.map((r) => (
                    <Row
                        key={r}
                        icon={
                            r.startsWith("The packet") ? (
                                <Mail className="h-4 w-4" />
                            ) : (
                                <RefreshCw className="h-4 w-4" />
                            )
                        }
                        tone="redo"
                    >
                        {r}
                    </Row>
                ))}
            </Box>
            {plan.warnings.length > 0 && (
                <Box title="Look at these" tone="warn">
                    {plan.warnings.map((w) => (
                        <Row key={w.code} icon={<TriangleAlert className="h-4 w-4" />} tone="warn">
                            {w.message}
                        </Row>
                    ))}
                </Box>
            )}
            <Box title="Stays the same" tone="stay">
                {plan.stays.map((s) => (
                    <Row key={s} icon={<MapPin className="h-4 w-4" />} tone="stay">
                        {s}
                    </Row>
                ))}
            </Box>
        </div>
    );
}

function stepIcon(step: string) {
    if (step === "rebuilt") return <CheckCircle2 className="h-4 w-4 text-emerald-700" />;
    if (step === "failed" || step === "no_paper") return <TriangleAlert className="h-4 w-4 text-amber-700" />;
    return <RefreshCw className="h-4 w-4 text-muted-foreground" />;
}

function MoveDone({
    done,
    jobId,
    onClose,
}: {
    done: { rebuild: RebuildOutcome; lastMove: MoveRecord | null; to: string };
    jobId: string;
    onClose: () => void;
}) {
    const undo = useUndoMove(jobId, onClose);
    return (
        <div className="space-y-4" data-testid="move-job-done">
            <div className="flex items-center gap-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3">
                <CheckCircle2 className="h-7 w-7 shrink-0 text-emerald-700" />
                <p className="text-sm font-bold text-emerald-950">This job is now under {done.to}.</p>
            </div>
            <ul className="space-y-2">
                <li className="flex items-start gap-2 text-sm">
                    {stepIcon(done.rebuild.workOrder.step)}
                    <span>{done.rebuild.workOrder.message}</span>
                </li>
                <li className="flex items-start gap-2 text-sm">
                    {stepIcon(done.rebuild.reports.step)}
                    <span>{done.rebuild.reports.message}</span>
                </li>
            </ul>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-3">
                {done.lastMove?.canUndo && (
                    <Button variant="outline" onClick={() => undo.mutate(done.lastMove!.id)} disabled={undo.isPending}>
                        <Undo2 className="mr-1.5 h-4 w-4" /> Undo the move
                    </Button>
                )}
                <Button onClick={onClose}>Done</Button>
            </div>
        </div>
    );
}

function useUndoMove(jobId: string, after?: () => void) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (moveId: string) =>
            api.post<{ ok: true }>(`/api/jobs/${jobId}/move-customer/${moveId}/undo`, {}, { timeoutMs: 180_000 }),
        onSuccess: () => {
            toast.success("Moved back. Everything is the way it was.");
            void qc.invalidateQueries();
            after?.();
        },
        onError: (err) => {
            const msg = err instanceof ApiError ? ((err.body as { message?: string } | null)?.message ?? null) : null;
            toast.error(msg ?? "Undo didn't work. Nothing was changed.");
        },
    });
}

// ─── Entry points ─────────────────────────────────────────────────────────

/** "Wrong customer?" — the small button by the customer name. Office only.
 *  Holds no queries itself (it sits in headers that render everywhere); the
 *  dialog mounts — and asks the server anything — only once it's opened. */
export function MoveJobLink({ jobId, className }: { jobId: string; className?: string }) {
    const auth = useOptionalAuth();
    const [open, setOpen] = useState(false);
    if (!(auth?.can(PERMISSIONS.officeReview.manage) ?? false)) return null;
    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className={cn(
                    "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-semibold text-muted-foreground underline underline-offset-2 hover:text-foreground",
                    className,
                )}
                data-testid="move-job-link"
            >
                Wrong customer?
            </button>
            {open && <MoveJobDialog jobId={jobId} open onClose={() => setOpen(false)} />}
        </>
    );
}

/**
 * The warning when the paper names another vendor, and the "moved today"
 * strip with Undo. Renders nothing when there's nothing to say.
 */
export function JobCustomerCheck({ jobId, compact = false }: { jobId: string; compact?: boolean }) {
    const auth = useOptionalAuth();
    const canMove = auth?.can(PERMISSIONS.officeReview.manage) ?? false;
    const statusQ = useJobMoveStatus(auth ? jobId : null);
    const [open, setOpen] = useState(false);
    const undo = useUndoMove(jobId);
    const s = statusQ.data;
    const issuer = s?.issuer ?? null;
    const last = s?.lastMove ?? null;
    const showMoved = !!last?.movedAt && !last.undoneAt && sameDay(last.movedAt);
    if (!issuer || (issuer.mismatch !== true && !showMoved)) return null;
    const suggested =
        issuer.paperCustomerId && issuer.paperCustomerId !== issuer.customerId
            ? { id: issuer.paperCustomerId, name: issuer.paperCustomerName ?? issuer.printedName ?? "Customer" }
            : null;
    return (
        <div className={cn("space-y-2", compact ? "" : "mb-3")}>
            {issuer.mismatch && (
                <div
                    className="flex flex-wrap items-center gap-3 rounded-lg border-2 border-amber-400 bg-amber-50 px-3 py-2.5"
                    data-testid="wrong-customer-warning"
                >
                    <TriangleAlert className="h-6 w-6 shrink-0 text-amber-700" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-amber-950">Wrong customer?</p>
                        <p className="text-xs text-amber-900">
                            The work order paper says <b>{issuer.printedName}</b>. This job is under{" "}
                            <b>{issuer.customerName}</b>, so its paper, report, and packet use the wrong company.
                        </p>
                    </div>
                    {canMove && (
                        <Button size="sm" onClick={() => setOpen(true)} data-testid="wrong-customer-move">
                            Move this job{suggested ? ` to ${suggested.name}` : ""}
                        </Button>
                    )}
                </div>
            )}
            {showMoved && last && (
                <div
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-sky-300 bg-sky-50 px-3 py-2"
                    data-testid="job-moved-strip"
                >
                    <RefreshCw className="h-5 w-5 shrink-0 text-sky-700" />
                    <p className="min-w-0 flex-1 text-xs text-sky-950">
                        Moved today from <b>{last.fromCustomerName ?? "another customer"}</b> to{" "}
                        <b>{last.toCustomerName ?? "this customer"}</b>
                        {last.movedByName ? ` by ${last.movedByName}` : ""}.
                        {!last.canUndo && last.undoBlockedReason ? ` Undo is off: ${last.undoBlockedReason}` : ""}
                    </p>
                    {canMove && last.canUndo && (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => undo.mutate(last.id)}
                            disabled={undo.isPending}
                        >
                            <Undo2 className="mr-1.5 h-4 w-4" /> Undo
                        </Button>
                    )}
                </div>
            )}
            {canMove && (
                <MoveJobDialog jobId={jobId} open={open} onClose={() => setOpen(false)} suggestedCustomer={suggested} />
            )}
        </div>
    );
}
